import { breakpoints } from '../utils/global-styles';

declare const window: Window;

import React, { useLayoutEffect, useState } from 'react';
import { useRoutes } from '../routing/routes';
import { ButtonA, ButtonLink } from './button';
import styled from '@emotion/styled';
import { textColors } from '../utils/color-palette';
import { navigate } from 'gatsby-plugin-react-intl';
import { GoThreeBars, GoX } from 'react-icons/go';
import { rhythm } from '../utils/typography';
import { css } from '@emotion/core';
import { LanguageToggle } from './language-toggle';

export const UlMobileStyled = styled.ul<{ isOpen: boolean; scrolled: boolean }>`
  all: unset;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  background: white;
  color: ${textColors.secondaryLight};
  display: flex;
  flex-flow: column;
  justify-content: space-around;

  transform: translateY(
    ${({ isOpen, scrolled }) =>
      isOpen ? `${scrolled ? '32px' : '0'}` : '-100%'}
  );

  transition: transform 0.3s;

  &::after {
    position: absolute;
    content: '';
    width: 1000vw;
    height: 1000vh;
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    z-index: 2;
  }
`;

const UlDesktop = styled.ul`
  display: flex;
`;

const LiStyled = styled.li`
  all: unset;
  z-index: 3;
  position: relative;
  font-weight: 600;
  color: ${textColors.secondary};
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MobileButtonStyles = css`
  position: sticky;
  z-index: 3;
  width: ${rhythm(1)};
  height: ${rhythm(1)};
  color: ${textColors.secondary};
`;

const MobileMenuButtonStyled = styled(GoThreeBars)`
  ${MobileButtonStyles};
`;

const MobileXButtonStyled = styled(GoX)`
  ${MobileButtonStyles};
`;

export const MobileMenuWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const Navigation = ({
  className,
  scrolled,
}: {
  className?: string;
  scrolled: boolean;
}) => {
  const [isDesktop, setIsDesktop] = useState(true);
  useLayoutEffect(() => {
    if (window) {
      const matcher = window.matchMedia(breakpoints.navigationMenu);
      const listener = ({ matches }: { matches: boolean }) =>
        setIsDesktop(matches);
      listener(matcher);
      matcher.addListener(listener);

      return () => {
        matcher.removeListener(listener);
      };
    }
  });

  return isDesktop ? (
    <DesktopNavigation className={className}></DesktopNavigation>
  ) : (
    <MobileNavigation
      scrolled={scrolled}
      css={{ marginLeft: 'auto' }}
    ></MobileNavigation>
  );
};

const DesktopNavigation = ({ className }: { className?: string }) => {
  const routes = useRoutes();

  return (
    <>
      <UlDesktop className={className}>
        {routes.map(route => (
          <LiStyled key={route.url}>
            <ButtonLink to={route.url} activeClassName={'active'}>
              {route.label}
            </ButtonLink>
          </LiStyled>
        ))}
        <LiStyled>
          <LanguageToggle></LanguageToggle>
        </LiStyled>
      </UlDesktop>
    </>
  );
};

const MobileNavigation = ({
  className,
  location,
  scrolled,
}: {
  scrolled: boolean;
  className?: string;
  location?: { pathname: string };
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [nextUrl, setNextUrl] = useState(location?.pathname);
  const onSetIsOpen = (toState: boolean) => () => setIsOpen(toState);
  const onTransitionEnd = () =>
    nextUrl && nextUrl !== location?.pathname && navigate(nextUrl);
  const onNavigation = (to: string) => () => {
    setNextUrl(to);
    setIsOpen(false);
  };

  const routes = useRoutes();

  return (
    <MobileMenuWrapper className={className}>
      {isOpen ? (
        <MobileXButtonStyled onClick={onSetIsOpen(false)}></MobileXButtonStyled>
      ) : (
        <MobileMenuButtonStyled
          onClick={onSetIsOpen(true)}
        ></MobileMenuButtonStyled>
      )}
      <UlMobileStyled
        scrolled={scrolled}
        isOpen={isOpen}
        onClick={onSetIsOpen(false)}
        onTransitionEnd={onTransitionEnd}
      >
        {routes.map(route => (
          <LiStyled key={route.url}>
            <ButtonA
              onClick={onNavigation(route.url)}
              className={location?.pathname === route.url ? 'active' : ''}
            >
              {route.label}
            </ButtonA>
          </LiStyled>
        ))}
        <LiStyled>
          <LanguageToggle></LanguageToggle>
        </LiStyled>
      </UlMobileStyled>
    </MobileMenuWrapper>
  );
};
